import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {findNodeHandle, useWindowDimensions, View} from 'react-native'
import {type ModerationDecision, moderatePost} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {countWorkKinds, type ProfileWorkItem} from '#/lib/profileWork'
import {useBottomBarOffset} from '#/lib/hooks/useBottomBarOffset'
import {type NavigationProp} from '#/lib/routes/types'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {List, type ListRef} from '#/view/com/util/List'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {EditBig_Stroke1_Corner0_Rounded as EditIcon} from '#/components/icons/EditBig'
import {Text} from '#/components/Typography'
import {
  AddCapsuleCard,
  AddFlashbookCard,
  AddModuleCard,
  CapsuleCell,
  DraftCell,
  FlashbookCell,
  ModuleCell,
  WorkGridRow,
  WorkKindSwitch,
  workAspect,
  workColumns,
  workRowKey,
  workRows,
  type WorkEntry,
  type WorkKind,
  type WorkKindFilter,
  type WorkRow,
} from '#/components/work'
import {IS_IOS} from '#/env'

export interface SectionRef {
  scrollToTop: () => void
}

/** The author facts a grid cell needs, so a cell can name whose work it draws. */
type WorkAuthor = {
  did: string
  handle: string
  displayName?: string
  avatar?: string
}

/**
 * One of a profile's two work tabs: the account's own capsules, or its own books.
 *
 * It is the classroom's grid, one screen over — the same cells, the same two-up
 * (three-up on a wide screen) rows, the same first-cell way in — because a grid of
 * work is a grid of work wherever the work is being looked at, and two grids drawn
 * to the same idea would only ever be two things to keep in step
 * (see `#/components/work`).
 *
 * What differs is whose work it is and what a reader can do with it:
 *
 *  • **The creator's own page** shows everything they have made — published work,
 *    and their unfinished work beside it, badged and opening in the studio, because
 *    finding the book you were in the middle of is the one thing a creator wants
 *    from their own page.
 *  • **Somebody else's page** shows only what that account published, and nothing
 *    that might be continued or shared: no drafts, no work that only a room holds
 *    enough of what it needs. Which records those are is `#/lib/profileWork`'s rule,
 *    applied before this section is handed anything.
 *
 * A visitor never sees the empty state, because the tab is only there when there is
 * something in it (see `#/view/screens/Profile`); the creator sees the words and the
 * way in.
 *
 * ## Two kinds in one tab
 *
 * The **Capsules** tab holds the account's capsules *and* its modules, because a
 * module is a playlist of capsules rather than a shelf of its own. `items` is
 * therefore whatever the profile decided to show, of either kind, and this section
 * is what turns that into a choice: a filter row when the tab holds more than one
 * kind, and cells that draw each item as what it is. The `kind` prop stays what it
 * always was — which tab this is — and is what the grid falls back to when the
 * reader has filtered to a single kind, so a tab of one kind behaves exactly as it
 * did before modules existed.
 *
 * A draft opens in its *own* studio, which is the one place the two kinds give
 * different answers to the same question (see `onContinue`).
 */
export function ProfileWorkSection({
  ref,
  kind,
  items,
  author,
  isMe,
  scrollElRef,
  headerHeight,
  isFocused,
  setScrollViewTag,
}: {
  ref?: React.Ref<SectionRef>
  kind: WorkKind
  /** The account's own work, already filtered for who is looking (see the note). */
  items: ProfileWorkItem[]
  author: WorkAuthor
  isMe: boolean
  scrollElRef: ListRef
  headerHeight: number
  isFocused: boolean
  setScrollViewTag: (tag: number | null) => void
}) {
  const t = useTheme()
  const {height} = useWindowDimensions()
  const bottomBarOffset = useBottomBarOffset(100)
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {gtMobile} = useBreakpoints()

  /**
   * Which kind the grid is showing.
   *
   * `all` is the default, and it is what the tab is for. State rather than a route
   * param: it is a way of looking at one screen, and a reader who comes back to the
   * tab expecting their place would rather have the whole tab than a filtered one
   * they cannot explain.
   */
  const [filter, setFilter] = useState<WorkKindFilter>('all')

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {},
  }))

  useEffect(() => {
    if (IS_IOS && isFocused && scrollElRef.current) {
      const nativeTag = findNodeHandle(scrollElRef.current)
      setScrollViewTag(nativeTag)
    }
  }, [isFocused, scrollElRef, setScrollViewTag])

  const postUris = useMemo(
    () => items.map(item => item.postUri).filter((uri): uri is string => !!uri),
    [items],
  )
  const {data: postViews, isPending} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)

  /**
   * Moderation for the published work, once for the whole grid.
   *
   * A capsule's card is drawn through `Hider`, so it needs a decision like any
   * other piece of content — and the posts arrive in a batch, so it is cheaper to
   * decide them together than to ask the same question per card. A book's card has
   * no hider to hand one to (the same book in a feed is moderated through `Post`),
   * which is the one thing the two cells do differently.
   */
  const moderationOpts = useModerationOpts()
  const moduiByUri = useMemo(() => {
    const map = new Map<string, ModerationDecision>()
    if (!moderationOpts) return map
    for (const post of postViews ?? []) {
      map.set(post.uri, moderatePost(post, moderationOpts))
    }
    return map
  }, [moderationOpts, postViews])

  /**
   * The kind the grid is drawing, once the filter has had its say.
   *
   * Everything that has to name one kind — the way in, the empty state, the
   * proportions of a cell standing in for work — reads this rather than the tab, so
   * a reader who has asked for modules sees a module's way in and a module's empty
   * state.
   */
  const counts = useMemo(() => countWorkKinds(items), [items])
  /*
   * When the choice is offered, which is a question about *reach* rather than
   * tidiness.
   *
   * The Capsules tab is the only place in the app a creator can start a module from
   * their own page, so on their own page the row is always there — including with a
   * `Modules 0` beside it, which is the whole point: a zero is how a creator finds
   * out the feature exists. On somebody else's page there is nothing to start, so
   * the row appears only once that account has actually made one, and a visitor is
   * not shown a filter whose only other position is empty. Books never carry it: the
   * Flashbooks tab holds one kind of thing.
   */
  const showSwitch = kind === 'capsule' && (isMe || (counts.module ?? 0) > 0)
  const shown = useMemo(
    () =>
      filter === 'all' ? items : items.filter(item => item.kind === filter),
    [filter, items],
  )
  const gridKind: WorkKind = filter === 'all' ? kind : filter

  /** The way in: the studio, for work that is about to become this profile's. */
  const onWrite = useCallback(() => {
    navigation.navigate(
      gridKind === 'module'
        ? 'ModuleStudio'
        : gridKind === 'flashbook'
          ? 'FlashBookStudio'
          : 'CapsuleStudio',
    )
  }, [gridKind, navigation])

  /**
   * Continuing a draft is opening the studio on it (see `#/lib/routes/types`).
   *
   * The item's own kind, not the tab's: a module draft is a half-built playlist
   * with six places in it, and the capsule studio would open it as nothing at all.
   */
  const onContinue = useCallback(
    (item: ProfileWorkItem) => {
      if (item.kind === 'module') {
        navigation.navigate('ModuleStudio', {draftUri: item.uri})
      } else if (item.kind === 'flashbook') {
        navigation.navigate('FlashBookStudio', {draftUri: item.uri})
      } else {
        navigation.navigate('CapsuleStudio', {draftUri: item.uri})
      }
    },
    [navigation],
  )

  /**
   * The grid's rows (see `#/components/work`).
   *
   * Published work and drafts are one list here, in one order: the question this
   * tab answers is what the account has made, and a draft written this morning
   * belongs where its work began. The way in comes first, and only on the creator's
   * own page and only once there is something to sit beside it — the same two
   * conditions a classroom's grid uses.
   */
  const columns = workColumns(gtMobile)
  const rows = useMemo(
    () =>
      workRows(shown, {
        columns,
        keyOf: item => item.uri,
        withAdd: isMe && shown.length > 0,
      }),
    [columns, isMe, shown],
  )

  const entryFor = useCallback(
    (item: ProfileWorkItem): WorkEntry => ({
      id: item.uri,
      text: item.description,
      capsuleUri: item.kind === 'capsule' ? item.uri : undefined,
      capsuleTitle: item.kind === 'capsule' ? item.title : undefined,
      flashbookUri: item.kind === 'flashbook' ? item.uri : undefined,
      flashbookTitle: item.kind === 'flashbook' ? item.title : undefined,
      moduleUri: item.kind === 'module' ? item.uri : undefined,
      moduleTitle: item.kind === 'module' ? item.title : undefined,
      postUri: item.postUri,
      authorDid: author.did,
      authorHandle: author.handle,
      authorName: author.displayName,
      authorAvatar: author.avatar,
      createdAt: item.createdAt,
    }),
    [author.avatar, author.did, author.displayName, author.handle],
  )

  const renderRow = useCallback(
    ({item: row}: {item: WorkRow<ProfileWorkItem>; index: number}) => (
      <WorkGridRow
        cells={row.cells}
        renderAdd={() =>
          gridKind === 'module' ? (
            <AddModuleCard
              testID="profileModulesAdd"
              label={l`Write a module`}
              caption={l`Six capsules, in order, as one run.`}
              onPress={onWrite}
            />
          ) : gridKind === 'flashbook' ? (
            <AddFlashbookCard
              testID="profileFlashbooksAdd"
              label={l`Write a FlashBook`}
              caption={l`Start a new one, or continue a draft.`}
              onPress={onWrite}
            />
          ) : (
            <AddCapsuleCard
              testID="profileCapsulesAdd"
              label={l`Write a capsule`}
              caption={l`Start a new one, or continue a draft.`}
              onPress={onWrite}
            />
          )
        }
        renderWork={item =>
          /*
           * A draft has no post, so it is drawn from its record and opens in the
           * studio; published work is drawn from its post, like the classroom's
           * grid and the feed do (see `DraftCell`).
           *
           * The item's own kind decides the rest, and it has to: with the filter on
           * *All* one row can legitimately hold a capsule beside a module, so the
           * tab is not an answer to what to draw any more — the item is.
           */
          item.status === 'draft' ? (
            <DraftCell
              testID={`profileDraft-${item.uri}`}
              kind={item.kind}
              title={item.title}
              coverUri={item.coverUri}
              size={item.size}
              onPress={() => onContinue(item)}
            />
          ) : item.kind === 'capsule' ? (
            <CapsuleCell
              entry={entryFor(item)}
              post={item.postUri ? viewsByUri.get(item.postUri) : undefined}
              modui={item.postUri ? moduiByUri.get(item.postUri) : undefined}
              isPending={isPending}
              testID={`profileCapsule-${item.uri}`}
              goneTestID={`profileCapsuleGone-${item.uri}`}
            />
          ) : item.kind === 'module' ? (
            <ModuleCell
              entry={entryFor(item)}
              post={item.postUri ? viewsByUri.get(item.postUri) : undefined}
              isPending={isPending}
              testID={`profileModule-${item.uri}`}
              goneTestID={`profileModuleGone-${item.uri}`}
            />
          ) : (
            <FlashbookCell
              entry={entryFor(item)}
              post={item.postUri ? viewsByUri.get(item.postUri) : undefined}
              isPending={isPending}
              testID={`profileFlashbook-${item.uri}`}
              goneTestID={`profileFlashbookGone-${item.uri}`}
            />
          )
        }
      />
    ),
    [
      entryFor,
      gridKind,
      isPending,
      l,
      moduiByUri,
      onContinue,
      onWrite,
      viewsByUri,
    ],
  )

  const empty = isMe ? (
    <OwnerEmpty kind={gridKind} onWrite={onWrite} />
  ) : (
    <View style={[a.px_lg, a.py_4xl, a.align_center]}>
      <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_medium]}>
        {l`Nothing published yet.`}
      </Text>
    </View>
  )

  return (
    <View
      testID={`profile${kind === 'capsule' ? 'Capsules' : 'Flashbooks'}Section`}>
      <List
        testID={`profile${kind === 'capsule' ? 'Capsules' : 'Flashbooks'}List`}
        ref={scrollElRef}
        data={rows}
        renderItem={renderRow}
        keyExtractor={workRowKey}
        /*
         * The filter rides in the list's header, so it scrolls with the grid as
         * one page rather than sitting over it — which is what keeps the tab a
         * single screen that can be paged to.
         */
        ListHeaderComponent={
          showSwitch ? (
            <WorkKindSwitch
              value={filter}
              onChange={setFilter}
              counts={counts}
              label={
                kind === 'capsule'
                  ? l`Your capsules and your playlists`
                  : l`Work`
              }
              testIDPrefix={`profile${kind === 'capsule' ? 'Capsules' : 'Flashbooks'}`}
            />
          ) : null
        }
        headerOffset={headerHeight}
        contentContainerStyle={{
          minHeight: height + headerHeight,
          paddingBottom: bottomBarOffset,
        }}
        removeClippedSubviews
        desktopFixedHeight
        ListEmptyComponent={empty}
        onRefresh={() => {}}
        refreshing={false}
      />
    </View>
  )
}

/**
 * The creator's own empty tab.
 *
 * A profile with no books yet is not an error state, it is a page that has nothing
 * on it but is theirs — so it says what is missing and hands them the one thing to do
 * about it, in the same words the classroom's empty tabs use for the same job.
 *
 * It is told *which* kind is missing, so a reader who has filtered the Capsules tab to
 * its modules and found none is offered a module rather than a capsule — the same "say
 * what is missing" rule, applied to the filter they chose.
 */
function OwnerEmpty({kind, onWrite}: {kind: WorkKind; onWrite: () => void}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_md]}>
      <EditIcon size="3xl" fill={t.atoms.text_contrast_medium.color} />
      <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_high]}>
        {kind === 'module'
          ? l`No modules yet.`
          : kind === 'flashbook'
            ? l`No books yet.`
            : l`No capsules yet.`}
      </Text>
      <Button
        testID={`profileEmptyWrite-${kind}`}
        label={
          kind === 'module'
            ? l`Write a module`
            : kind === 'flashbook'
              ? l`Write a FlashBook`
              : l`Write a capsule`
        }
        color="primary"
        size="small"
        onPress={onWrite}>
        <ButtonIcon icon={EditIcon} />
        <ButtonText>
          {kind === 'module'
            ? l`Write a module`
            : kind === 'flashbook'
              ? l`Write a FlashBook`
              : l`Write a capsule`}
        </ButtonText>
      </Button>
    </View>
  )
}
